import classNames from 'classnames';
import React, { Component, PropsWithChildren, ReactElement, ReactPortal } from 'react';
import { createPortal } from 'react-dom';
import _Draggable from 'react-draggable';
import posed from 'react-pose';
import { createId } from '../utils/IdGenerator';
import './Modal.scss';
import { ModalBackdrop } from './ModalBackdrop';
import { ModalsStack } from './ModalsStack';
import { IModalProps, ModalContext } from './types';

const Draggable = _Draggable as unknown as React.AsFCC<typeof _Draggable>;

const modalsStack = new ModalsStack();

export interface IModalState {
  isTop: boolean;
  mzIndex: number;
  bzIndex: number;
  visible: boolean;
}

const KEY_ESC = 27;
const KEY_TAB = 9;
const FULLSCREEN = 'fullscreen';

// How fast to animate
const TRANSITION_DURATION = 200;

interface IDynamicProps {
  style: {
    transform: string;
  };
}

function getTranslatedXY(transform: string): { x: number; y: number } {
  const getTranslateXYfromTransformString = /translate\(\s?(-?\d+)px,\s?(-?\d+)px\)\s?/;

  if (!transform) {
    return { x: 0, y: 0 };
  }

  const [, x, y] = transform.split(getTranslateXYfromTransformString);

  return { x: Number(x), y: Number(y) };
}

const PosedModal = posed.div({
  /* tslint:disable object-literal-sort-keys */
  beforeEnter: {
    opacity: 0,
    scale: 0.8,
  },
  enter: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: TRANSITION_DURATION,
    },
  },
  exit: {
    opacity: 0,

    // Since the dialog may have been dragged to a different position, we need
    // to fix the x and y position on our way out so the modal doesn't jump
    x: ({ style: { transform } }: IDynamicProps) => {
      return getTranslatedXY(transform).x;
    },
    y: ({ style: { transform } }: IDynamicProps) => {
      return getTranslatedXY(transform).y;
    },

    transition: {
      duration: TRANSITION_DURATION,
      // Make sure that snapping to x / y happens instantaneously
      x: { duration: 0 },
      y: { duration: 0 },
    },
  },
  /* tslint:enable object-literal-sort-keys */
});

export class Modal extends Component<PropsWithChildren<IModalProps>, IModalState> {
  public static defaultProps = {
    autofocus: true,
    backdrop: true,
    draggable: true,
    keyboard: true,
  };
  public readonly state: Readonly<IModalState>;
  // modal component id
  private id: string;
  // referense to a modal element when it is visible
  private node: HTMLElement | null;
  // we will restore focus to this element on modal close
  private lastFocused: HTMLElement | null;

  constructor(props: IModalProps) {
    super(props);

    const { show } = props;

    this.id = createId('modal');
    if (show) {
      modalsStack.add(this.id, this.onModalsStackChange);
    }

    const { bzIndex, isTop, mzIndex } = modalsStack.getPosition(this.id);
    this.state = { bzIndex, isTop, mzIndex, visible: show };
  }

  // updates isTop and zindex values
  public onModalsStackChange = () => {
    const { bzIndex, isTop, mzIndex } = modalsStack.getPosition(this.id);
    this.setState({ bzIndex, isTop, mzIndex });
  };

  public componentDidMount() {
    if (this.props.autofocus && this.props.show) {
      this.catchFocusOnShow();
    }
    window.document.addEventListener('keydown', this.onKeyDown);
  }

  public componentWillUnmount() {
    this.restoreFocus();
    modalsStack.remove(this.id);
    window.document.removeEventListener('keydown', this.onKeyDown);
  }

  public componentDidUpdate(prevProps: IModalProps) {
    const didShow = this.props.show && !prevProps.show;
    const didHide = !this.props.show && prevProps.show;

    // The show prop lets users of this prop control visibility; then we control
    // our internal visibility via state so that we can have animated
    // transitions to show and hide
    if (didShow) {
      this.setState({ visible: true });
    }

    if (this.props.autofocus) {
      if (didShow) {
        this.catchFocusOnShow();
      }
      if (didHide) {
        modalsStack.remove(this.id);
        this.restoreFocus();
      }
    }
  }

  public handleClose = () => {
    this.props.onHide();
  };

  public poseComplete = (pose: string) => {
    if (pose === 'exit') {
      this.setState({ visible: false });
    }
  };

  /**
   * Handle close on 'esc'. Keep focus inside modal
   * @param e
   */
  public onKeyDown = (e: KeyboardEvent) => {
    if (!this.state.isTop) {
      return;
    }

    // close on "ESC" key
    if (this.props.keyboard && e.keyCode === KEY_ESC) {
      e.stopImmediatePropagation();
      this.handleClose();
    }

    // focus trap
    if (this.props.autofocus && e.keyCode === KEY_TAB && this.node) {
      const focusable = this.node.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]',
      );
      if (!focusable.length) {
        this.focusDialog();
        e.preventDefault();
        return;
      }
      const firstFocusable = focusable[0] as HTMLElement;
      const lastFocusable = focusable[focusable.length - 1] as HTMLElement;
      // moving forward - focus the div.modal when focus out from the last tabbable element
      if (e.target === lastFocusable && !e.shiftKey) {
        this.focusDialog();
        e.preventDefault();
      }
      // moving backward - focus the div.modal when focus out from the first tabbable element
      if (e.target === firstFocusable && e.shiftKey) {
        this.focusDialog();
        e.preventDefault();
      }
      // moving backward - focus the last tabbable when focus out from div.modal
      if (e.target === this.node && e.shiftKey) {
        lastFocusable.focus();
        e.preventDefault();
      }
    }
  };

  public render() {
    if (!this.state.visible) {
      return null;
    }

    const isFullScreen = this.props.size === FULLSCREEN;
    const context = {
      draggable: this.props.draggable ? true : false,
      handleClose: this.handleClose,
      isFullScreen,
    };

    const dialogClasses = classNames('modal-dialog', {
      ['modal-sm']: this.props.size === 'small',
      ['modal-lg']: this.props.size === 'large',
      ['modal-fullscreen']: isFullScreen,
      ['modal-slender']: this.props.size === 'slender',
    });
    const dialogContent = (
      <PosedModal
        initialPose="beforeEnter"
        pose={this.props.show ? 'enter' : 'exit'}
        onPoseComplete={this.poseComplete}
        className={dialogClasses}
        role={'document'}
        style={{ zIndex: this.state.mzIndex }}
      >
        <div className={`modal-content`}>{this.props.children}</div>
      </PosedModal>
    );

    const modal = (
      <ModalContext.Provider value={context}>
        <div
          className={'modal hlc-modal'}
          style={{
            display: 'block',
            zIndex: this.state.mzIndex,
          }}
          tabIndex={-1}
          role={'dialog'}
          ref={this.setNodeRef}
          aria-labelledby={this.props.ariaLabelledby}
          aria-describedby={this.props.ariaDescribedby}
          qa-id={this.props['qa-id']}
        >
          {this.maybeDraggable(dialogContent)}
        </div>
        {this.maybeBackdrop()}
      </ModalContext.Provider>
    );

    return createPortal(modal, window.document.body) as ReactPortal;
  }

  public setNodeRef = (ref: HTMLElement | null) => {
    this.node = ref;
  };

  private maybeBackdrop() {
    const { bzIndex } = this.state;
    const { onHide, show, backdrop } = this.props;

    if (backdrop !== false) {
      const qaId = this.props['qa-id'] ? this.props['qa-id'] + '-backdrop' : undefined;

      return <ModalBackdrop qa-id={qaId} pose={show ? 'enter' : 'exit'} zIndex={bzIndex} onHide={onHide} />;
    }
    return null;
  }

  private maybeDraggable(element: ReactElement<any>) {
    if (this.props.draggable === true) {
      return (
        <Draggable handle={'.modal-header'} onStart={this.onDragStart}>
          {element}
        </Draggable>
      );
    }
    return element;
  }

  private catchFocusOnShow() {
    modalsStack.add(this.id, this.onModalsStackChange);
    this.onModalsStackChange();

    this.lastFocused = document.activeElement as HTMLElement;
    this.focusDialog();
  }

  private focusDialog() {
    if (this.props.autofocus && this.node) {
      this.node.focus();
    }
  }

  private restoreFocus() {
    if (this.props.autofocus && this.lastFocused) {
      // in case when Modal was triggered by Icon - SVG becomes last focused element.
      // IE doesn't support "focus" on SVG.
      // here we check the closest focusable element to put focus back to it.
      const lastFocused = findFocusable(this.lastFocused);
      if (lastFocused) {
        lastFocused.focus();
        this.lastFocused = null;
      }
    }
  }

  // do not start drag when user click on close button on the header
  private onDragStart = (e: React.MouseEvent<HTMLElement>): false | void => {
    if (!this.node) {
      return false;
    }
    const notDraggable = this.node.querySelector('.cancel-dragging');
    const target = e.target as HTMLElement;
    if (target && notDraggable && (notDraggable === target || notDraggable.contains(target))) {
      return false;
    }
  };
}

function findFocusable(element: HTMLElement | null): HTMLElement | null {
  while (element && typeof element.focus === 'undefined') {
    element = element.parentNode as HTMLElement;
  }
  return element;
}

export default Modal;
